import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { FiSearch, FiPlus, FiEdit, FiTrash2, FiDownload, FiCheckCircle, FiCreditCard, FiUsers, FiXCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import PassageiroModal from '../components/PassageiroModal';
import ParcelasModal from '../components/ParcelasModal';

const Passageiros = () => {
  const [passageiros, setPassageiros] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [totalPassageiros, setTotalPassageiros] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroViagem, setFiltroViagem] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [passageiroEdit, setPassageiroEdit] = useState(null);
  const [parcelasModalOpen, setParcelasModalOpen] = useState(false);
  const [passageiroParcelas, setPassageiroParcelas] = useState(null);

  useEffect(() => {
    fetchPassageiros();
    fetchViagens();
    fetchTotalPassageiros();
  }, [busca, filtroViagem, filtroStatus]);

  const fetchTotalPassageiros = async () => {
    try {
      const response = await api.get('/api/passageiros/total');
      setTotalPassageiros(response.data.total || 0);
    } catch (error) {
      console.error('Erro ao buscar total de passageiros');
    }
  };

  const fetchPassageiros = async () => {
    try {
      const params = new URLSearchParams();
      if (busca) params.append('busca', busca);
      if (filtroViagem) params.append('viagem_id', filtroViagem);
      if (filtroStatus) params.append('status', filtroStatus);

      const response = await api.get(`/api/passageiros?${params}`);
      setPassageiros(response.data);
    } catch (error) {
      toast.error('Erro ao carregar passageiros');
    } finally {
      setLoading(false);
    }
  };

  const fetchViagens = async () => {
    try {
      const response = await api.get('/api/viagens');
      setViagens(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens');
    }
  };

  const handleCancel = async (passageiro) => {
    if (
      !window.confirm(
        `Registrar desistência de ${passageiro.nome}?\n\n` +
          `Será feito: poltrona liberada, passageiro inativado (some da lista ativa), ` +
          `parcelas em aberto do carnê canceladas sem apagar o que já foi pago.`
      )
    )
      return;

    try {
      await api.post(`/api/passageiros/${passageiro.id}/cancelar`);
      toast.success('Desistência registrada: poltrona liberada e carnê encerrado (histórico mantido)');
      fetchPassageiros();
      fetchTotalPassageiros();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao cancelar passageiro');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este passageiro?')) return;

    try {
      await api.delete(`/api/passageiros/${id}`);
      toast.success('Passageiro excluído com sucesso');
      fetchPassageiros();
      fetchTotalPassageiros();
    } catch (error) {
      toast.error('Erro ao excluir passageiro');
    }
  };

  const handleEdit = (passageiro) => {
    setPassageiroEdit(passageiro);
    setModalOpen(true);
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (filtroViagem) params.append('viagem_id', filtroViagem);

      const response = await api.get(`/api/export/passageiros/${format}?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `passageiros.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Exportação realizada com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar dados');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Passageiros</h1>
          <p className="text-white mt-1">Gerencie os passageiros cadastrados</p>
        </div>
        <button
          onClick={() => {
            setPassageiroEdit(null);
            setModalOpen(true);
          }}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus className="mr-2" size={20} />
          Novo Passageiro
        </button>
      </div>

      {/* Card de Total de Passageiros */}
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-white bg-opacity-20 p-4 rounded-full">
              <FiUsers className="text-white" size={32} />
            </div>
            <div>
              <p className="text-white text-sm font-medium opacity-90">Total de Passageiros Cadastrados</p>
              <p className="text-white text-4xl font-bold mt-1">{totalPassageiros.toLocaleString('pt-BR')}</p>
              <p className="text-white text-xs mt-1 opacity-75">
                {passageiros.length === totalPassageiros 
                  ? 'Todos os passageiros estão sendo exibidos'
                  : `${passageiros.length} passageiro(s) exibido(s) com os filtros aplicados`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-sky-50 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por nome, CPF ou email..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
            />
          </div>
          <select
            value={filtroViagem}
            onChange={(e) => setFiltroViagem(e.target.value)}
            className="px-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
          >
            <option value="">Todas as viagens</option>
            {viagens.map((v) => (
              <option key={v.id} value={v.id}>{v.titulo}</option>
            ))}
          </select>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 bg-white border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 text-gray-800"
          >
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('excel')}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-white border border-sky-200 rounded-lg hover:bg-sky-100 text-gray-700"
              title="Exportar Excel"
            >
              <FiDownload size={20} />
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="flex-1 flex items-center justify-center px-4 py-2 bg-white border border-sky-200 rounded-lg hover:bg-sky-100 text-gray-700"
              title="Exportar CSV"
            >
              <FiDownload size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-sky-50 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-sky-200">
              <thead className="bg-sky-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pagamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-sky-200">
                {passageiros.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-600 bg-white">
                      Nenhum passageiro encontrado
                    </td>
                  </tr>
                ) : (
                  passageiros.map((passageiro) => {
                    // Extrair forma de pagamento das observações
                    let formaPagamento = passageiro.forma_pagamento 
                      ? passageiro.forma_pagamento.replace('Forma de pagamento: ', '') 
                      : '-';
                    
                    // Extrair informações de parcelamento e sinal para exibição
                    if (formaPagamento !== '-') {
                      const parcelasMatch = formaPagamento.match(/Parcelas: (\d+)x/i);
                      const sinalMatch = formaPagamento.match(/Sinal: R\$\s?([\d,\.]+)/i);
                      
                      // Formatar para exibição mais limpa
                      if (parcelasMatch) {
                        formaPagamento = formaPagamento.split(' | ')[0] + ` (${parcelasMatch[1]}x)`;
                      } else if (sinalMatch) {
                        formaPagamento = formaPagamento.split(' | ')[0] + ` (Sinal: R$ ${sinalMatch[1]})`;
                      } else {
                        formaPagamento = formaPagamento.split(' | ')[0];
                      }
                    }
                    
                    return (
                      <tr key={passageiro.id} className="hover:bg-sky-100 bg-white">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {passageiro.nome}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {passageiro.valor_passagem ? (
                            <span className="font-semibold">R$ {parseFloat(passageiro.valor_passagem).toFixed(2)}</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                passageiro.status_pagamento === 'pago'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {passageiro.status_pagamento === 'pago' ? 'Pago' : 'Pendente'}
                            </span>
                            {formaPagamento !== '-' && (
                              <span className="text-xs text-gray-500">{formaPagamento}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              passageiro.status === 'ativo'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {passageiro.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(passageiro)}
                              className="text-primary-600 hover:text-primary-900"
                              title="Editar passageiro"
                            >
                              <FiEdit size={18} />
                            </button>
                            {/* Botão para gerenciar parcelas - só aparece se for pagamento em carnê */}
                            {formaPagamento.includes('Carnê') && (
                              <button
                                onClick={() => {
                                  setPassageiroParcelas(passageiro);
                                  setParcelasModalOpen(true);
                                }}
                                className="text-purple-600 hover:text-purple-900"
                                title="Gerenciar parcelas do carnê"
                              >
                                <FiCreditCard size={18} />
                              </button>
                            )}
                            {passageiro.viagem_id && passageiro.status === 'ativo' && (
                              <button
                                onClick={() => handleCancel(passageiro)}
                                className="text-orange-500 hover:text-orange-700"
                                title="Cancelar viagem / liberar poltrona"
                              >
                                <FiXCircle size={18} />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(passageiro.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Excluir passageiro"
                            >
                              <FiTrash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <PassageiroModal
          passageiro={passageiroEdit}
          viagens={viagens}
          onClose={() => {
            setModalOpen(false);
            setPassageiroEdit(null);
          }}
          onSuccess={() => {
          fetchPassageiros();
          fetchTotalPassageiros();
          setModalOpen(false);
            setPassageiroEdit(null);
          }}
        />
      )}

      {parcelasModalOpen && passageiroParcelas && (
        <ParcelasModal
          passageiroId={passageiroParcelas.id}
          onClose={() => {
            setParcelasModalOpen(false);
            setPassageiroParcelas(null);
          }}
          onSuccess={() => {
            fetchPassageiros();
          }}
        />
      )}
    </div>
  );
};

export default Passageiros;
